import { swivuRecentRecordSortFn } from "@/modern/utils";
import type { BoardNote } from "@/bryntum-lib/models/BoardNote";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys";
import type { EntityTypeKey } from "@/other/constants/EntityManagerConfig";
import { EntityFinderHighlight } from "@/modern/components/entity-finder/components/EntityFinderHighlight";
import { EntityFinderItemRow, entityFinderTintText } from "@/modern/components/entity-finder/components/EntityFinderItemRow";
import { EntityFinderMetaBadge } from "@/modern/components/entity-finder/components/EntityFinderMetaBadge";
import { renderEntityFinderConfigIcon } from "@/modern/components/entity-finder/components/renderEntityFinderConfigIcon";
import { estimateDefaultRowHeight } from "@/modern/components/entity-finder/constants/config/shared";
import type { EntityFinderConfigEntry } from "@/modern/components/entity-finder/types";
import { WidgetParamKeys } from "@/types/entity";
import {
  defaultEntityListSection,
  defaultMatchesScope,
  matchesSearch,
  passesArchiveFilter,
  searchModeActive,
} from "@/modern/components/entity-finder/utils";

const NOTE_PARENT_TYPE_LABELS: Record<string, string> = {
  [ENTITY_TYPES.TASK]: "Task",
  [ENTITY_TYPES.RESOURCE]: "Resource",
  [ENTITY_TYPES.CLIENT]: "Client",
};

function getNoteParentEntityType(note: BoardNote): EntityTypeKey | null {
  if (note.taskId) return ENTITY_TYPES.TASK;
  if (note.resourceId) return ENTITY_TYPES.RESOURCE;
  if (note.clientId) return ENTITY_TYPES.CLIENT;
  return null;
}

function getNoteParentEntityId(note: BoardNote): string | number | null {
  if (note.taskId) return note.taskId;
  if (note.resourceId) return note.resourceId;
  if (note.clientId) return note.clientId;
  return null;
}

function getNoteParentTypeLabel(entityType: EntityTypeKey): string {
  return NOTE_PARENT_TYPE_LABELS[entityType] ?? entityType;
}

function getNoteParentLabel(note: BoardNote): string | undefined {
  const entityType = getNoteParentEntityType(note);
  const parent = note.entity as { name?: string } | null;
  if (!entityType || !parent?.name) return undefined;
  return `${getNoteParentTypeLabel(entityType)} · ${parent.name}`;
}

function noteMatchesSearch(note: BoardNote, query: string): boolean {
  const searchLower = query.trim().toLowerCase();
  if (!searchLower) return false;
  if (matchesSearch(note.name, searchLower)) return true;
  const parent = note.entity as { name?: string } | null;
  return matchesSearch(parent?.name, searchLower);
}

function isQueryableBoardNote(note: BoardNote): boolean {
  return !note.isPhantom && Boolean(getNoteParentEntityId(note));
}

function mapNote(note: BoardNote) {
  return {
    id: `note-${note.id}`,
    type: "note" as const,
    label: note.name || "Untitled note",
    subtitle: getNoteParentLabel(note),
    record: note,
  };
}

function queryNotes(ctx: Parameters<EntityFinderConfigEntry<"note">["getData"]>[0]) {
  return swivuRecentRecordSortFn(
    ctx.project.boardNotesStore.query(
      (note: BoardNote) =>
        isQueryableBoardNote(note) && passesArchiveFilter(note.archived, ctx.isShowArchive),
      true
    ) as unknown as BoardNote[],
    { currentBoardUserId: ctx.currentBoardUserId }
  );
}

export const noteConfig: EntityFinderConfigEntry<"note"> = {
  type: "note",
  label: "Note",
  plural: "Notes",
  icon: <i className="fa-regular fa-note-sticky" aria-hidden />,
  tint: "bg-sky-500/15 text-sky-600 dark:text-sky-400",
  listOrder: 75,
  showInFilter: true,
  isActive: searchModeActive,
  matchesScope: (ctx) => defaultMatchesScope(ctx, "note"),
  getData: (ctx) => {
    const trimmed = ctx.debouncedQuery.trim();
    if (!trimmed) return [];

    return queryNotes(ctx)
      .filter((note) => noteMatchesSearch(note, trimmed))
      .map(mapNote);
  },
  getRecentItem: (ctx) => {
    const records = queryNotes(ctx).slice(0, 1);
    return records[0] ? mapNote(records[0]) : null;
  },
  buildListSection: (items, _ctx) => defaultEntityListSection(noteConfig, items),
  estimateRowHeight: estimateDefaultRowHeight,
  getMetadata: (item) => {
    const note = item.record as BoardNote;
    const entityType = getNoteParentEntityType(note);
    if (!entityType) return null;
    const label = getNoteParentTypeLabel(entityType);
    return { text: label, variant: "status" as const };
  },
  itemRenderer: ({ item, query, ctx, metadata, entry }) => {
    const badge = metadata ?? noteConfig.getMetadata?.(item, ctx);

    return (
      <EntityFinderItemRow
        icon={renderEntityFinderConfigIcon(entry.icon, 14, "text-sm")}
        iconClassName={entityFinderTintText(noteConfig.tint)}
        title={<EntityFinderHighlight text={item.label} query={query} />}
        subtitle={
          item.subtitle ? (
            <EntityFinderHighlight text={item.subtitle} query={query} tone="muted" />
          ) : undefined
        }
        trailing={
          badge ? (
            <EntityFinderMetaBadge variant="status" title={badge.text}>
              {badge.text}
            </EntityFinderMetaBadge>
          ) : undefined
        }
      />
    );
  },
  onSelect: (item, ctx) => {
    const note = item.record as BoardNote;
    const entityType = getNoteParentEntityType(note);
    const entityId = getNoteParentEntityId(note);
    if (!entityType || entityId == null) return;

    const openEntityManager = ctx.entityManager.openEntityManager as (args: {
      entityType: string;
      entityId: string | number;
      otherParams?: Record<string, number | string>;
    }) => void;

    openEntityManager({
      entityType,
      entityId,
      otherParams: { [WidgetParamKeys.NOTE.EDIT]: note.id },
    });
  },
};
